/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import { ChangeDetectionStrategy, Component, Injector, OnInit } from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ApiResponse, BaseComponent, CartProductToCartSpecification, CartSpecification, SpecificationService} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {catchError, filter, map, takeUntil, tap} from 'rxjs/operators';
import { NbWindowRef } from "@nebular/theme";

@Component({
    selector: 'app-specification-address',
    templateUrl: './specification-popup.component.html',
    styleUrls: ['./specification-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class SpecificationPopupComponent extends BaseComponent implements OnInit {

  private products: number[];
  private specifications = new Set<number>();
  protected cartSpecification: CartSpecification;
  keyword = '';
  submitted = false;
  success = false;

  inputForm = new UntypedFormGroup({
    header: new UntypedFormControl('', Validators.required),
    description: new UntypedFormControl()
  });

  cartSpecifications: CartSpecification[] = [];
  cartSpecificationsFiltered: CartSpecification[] = [];
  private windowRef: NbWindowRef;

  constructor(private readonly specificationService: SpecificationService,
              private readonly injector: Injector
  ) {
    super();
    this.windowRef = this.injector.get(NbWindowRef);

  }

  ngOnInit(): void {
    this.loading = true;
    this.pull().pipe(catchError(err => this.handleError(err)), tap(response => this.handlePullResponse(response))).subscribe();
  }

  private handlePullResponse(response: CartSpecification[]) {
    this.cartSpecifications = this.cartSpecificationsFiltered = response;
    this.loading = false;
    this.cdr.detectChanges();
  }

  onSubmit() {
    this.submitted = true;
    const specification = {
      header: this.inputForm.value.header,
      description: this.inputForm.value.description,
      products: this.products
    };

    this.specificationService.createFromCart(specification).pipe(
      filter((resp: ApiResponse<CartSpecification>) => resp.success === true),
      tap(_ => this.handleCreateSpecificationResponse(_.data)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  private handleCreateSpecificationResponse(cartSpecification: CartSpecification) {
    this.cartSpecification = cartSpecification;
    this.submitted = false;
    this.inputForm.reset();
    this.success = true;
    this.cdr.detectChanges();
  }

  onSubmitSpecification() {
    this.submitted = true;
    this.pushSpecifications().pipe(
      map(response => this.handleResponse(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  private handleResponse(response: string) {
    this.submitted = false;
    if (response === 'done') {
      this.inputForm.reset();
      this.success = true;
    }
    this.cdr.detectChanges();
  }

  private pull(): Observable<CartSpecification[]> {
    return this.compoApiService.get<CartSpecification[]>('/specification').pipe(map(resp => resp.data));
  }

  toggle($event: boolean, id: number) {

    if ($event) {
      if (!this.specifications.has(id)) {
        this.specifications.add(id);
      }
    } else {
      if (this.specifications.has(id)) {
        this.specifications.delete(id);
      }
    }
  }

  private pushSpecifications(): Observable<string> {
    const data = {
      products: this.products,
      specifications: Array.from(this.specifications.values())
    };

    return this.compoApiService.post<CartProductToCartSpecification, string>('/specification/merge', data).pipe(map(resp => resp.data));
  }

  onKey($event: KeyboardEvent) {
    const regex = new RegExp(this.keyword, 'i');
    this.cartSpecificationsFiltered = this.cartSpecifications.filter(item => item.header.search(regex) !== -1);
  }

  hasSelected() {
    return this.specifications.size > 0;
  }

  protected goToSpecification() {
    this.windowRef.close();
    this.router.navigate(['cabinet','orders','specifications', this.cartSpecification.id]).then();
  }
}
